import React, { useState, Fragment, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  Phone,
  Person,
} from '@mui/icons-material';
import { clientContactsAPI } from '../../services/api';

const CONTACT_ROLES = ['VET', 'VET_TECH', 'BILLING', 'ADMIN', 'OTHER'];

const ClientContacts = ({ client, onContactUpdated }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'VET',
    vetLicenseNumber: '',
    hasPortalAccess: false,
    supervisingVetContactId: '',
  });

  useEffect(() => {
    loadContacts();
  }, [client?.id]);

  const loadContacts = async () => {
    if (!client?.id) return;
    
    setLoading(true);
    try {
      const response = await clientContactsAPI.getContacts(client.id);
      if (response.success && response.data) {
        setContacts(response.data);
      } else {
        setContacts([]);
      }
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'VET': return 'primary';
      case 'VET_TECH': return 'secondary';
      case 'BILLING': return 'warning';
      case 'ADMIN': return 'info';
      default: return 'default';
    }
  };

  const formatRole = (role) => {
    return role
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        role: contact.role || 'VET',
        vetLicenseNumber: contact.vetLicenseNumber || '',
        hasPortalAccess: contact.hasPortalAccess || false,
        supervisingVetContactId: contact.supervisingVetContactId || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'VET',
        vetLicenseNumber: '',
        hasPortalAccess: false,
        supervisingVetContactId: '',
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'VET',
      hasPortalAccess: false,
      supervisingVetContactId: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Invalid email format');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (editingContact) {
        const response = await clientContactsAPI.updateContact(editingContact.id, {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || null,
          role: formData.role,
          vetLicenseNumber: formData.vetLicenseNumber?.trim() || null,
          supervisingVetContactId: formData.supervisingVetContactId || null,
        });
        
        if (response.success) {
          // Update portal access separately if changed
          if (response.data.hasPortalAccess !== formData.hasPortalAccess) {
            await clientContactsAPI.updatePortalAccess(editingContact.id, {
              hasPortalAccess: formData.hasPortalAccess,
              canPlaceOrders: formData.hasPortalAccess,
            });
          }
        } else {
          throw new Error(response.message || 'Failed to update contact');
        }
      } else {
        const response = await clientContactsAPI.addContact({
          clientUserId: client.id,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || null,
          role: formData.role,
          vetLicenseNumber: formData.vetLicenseNumber?.trim() || null,
          hasPortalAccess: formData.hasPortalAccess,
          canPlaceOrders: formData.hasPortalAccess,
          supervisingVetContactId: formData.supervisingVetContactId || null,
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to add contact');
        }
      }

      handleCloseDialog();
      loadContacts();
      onContactUpdated();
    } catch (err) {
      setError(err.message || (editingContact ? 'Failed to update contact' : 'Failed to add contact'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await clientContactsAPI.removeContact(contactToDelete.id);
      
      if (response.success) {
        setDeleteConfirmOpen(false);
        setContactToDelete(null);
        loadContacts();
        onContactUpdated();
      } else {
        throw new Error(response.message || 'Failed to delete contact');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePortalAccess = async (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    
    const newAccess = !contact.hasPortalAccess;
    
    try {
      const response = await clientContactsAPI.updatePortalAccess(contactId, {
        hasPortalAccess: newAccess,
        canPlaceOrders: newAccess,
      });
      
      if (response.success) {
        loadContacts();
        onContactUpdated();
      } else {
        throw new Error(response.message || 'Failed to update portal access');
      }
    } catch (error) {
      console.error('Failed to update portal access:', error);
      setError('Failed to update portal access: ' + error.message);
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Practice Contacts
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Contact
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading && contacts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              No contacts yet. Add contacts to track multiple people at this practice.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {contacts.map((contact, index) => (
              <Fragment key={contact.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={contact.hasPortalAccess || false}
                            onChange={() => handleTogglePortalAccess(contact.id)}
                          />
                        }
                        label={
                          <Typography variant="caption" color={contact.hasPortalAccess ? 'success.main' : 'text.secondary'}>
                            Portal
                          </Typography>
                        }
                        sx={{ mr: 1 }}
                      />
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleOpenDialog(contact)}
                        title="Edit contact"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setContactToDelete(contact);
                          setDeleteConfirmOpen(true);
                          setError('');
                        }}
                        title="Delete contact"
                        sx={{ color: 'error.main' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                          {contact.firstName} {contact.lastName}
                        </Typography>
                        <Chip 
                          label={formatRole(contact.role)} 
                          size="small" 
                          color={getRoleColor(contact.role)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                              {contact.email}
                            </Typography>
                          </Box>
                          {contact.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                {contact.phone}
                              </Typography>
                            </Box>
                          )}
                          {contact.role === 'VET' && contact.vetLicenseNumber && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                                License: {contact.vetLicenseNumber}
                              </Typography>
                            </Box>
                          )}
                          {contact.supervisingVet && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Person fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                Supervised by: {contact.supervisingVet.firstName} {contact.supervisingVet.lastName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < contacts.length - 1 && <Divider />}
              </Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name *"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              disabled={loading}
              required
              fullWidth
            />
            <TextField
              label="Last Name *"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              disabled={loading}
              required
              fullWidth
            />
            <TextField
              label="Email *"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={loading}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={loading}
              fullWidth
            />
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>Role *</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    supervisingVetContactId: newRole === 'VET_TECH' ? formData.supervisingVetContactId : ''
                  });
                }}
                label="Role *"
              >
                {CONTACT_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {formatRole(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.role === 'VET' && (
              <TextField
                label="Vet License Number"
                value={formData.vetLicenseNumber}
                onChange={(e) => setFormData({ ...formData, vetLicenseNumber: e.target.value })}
                disabled={loading}
                fullWidth
                helperText="License number for this veterinarian"
              />
            )}
            {formData.role === 'VET_TECH' && (
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Assigned Vet (Optional)</InputLabel>
                <Select
                  value={formData.supervisingVetContactId || ''}
                  onChange={(e) => setFormData({ ...formData, supervisingVetContactId: e.target.value })}
                  label="Assigned Vet (Optional)"
                >
                  <MenuItem value="">
                    <em>None (Standalone Vet Tech)</em>
                  </MenuItem>
                  {contacts
                    .filter(c => c.role === 'VET' && c.id !== editingContact?.id)
                    .map((vet) => (
                      <MenuItem key={vet.id} value={vet.id}>
                        {vet.firstName} {vet.lastName}
                        {vet.email && ` (${vet.email})`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {!editingContact && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasPortalAccess}
                    onChange={(e) => setFormData({ ...formData, hasPortalAccess: e.target.checked })}
                    disabled={loading}
                  />
                }
                label="Enable portal access (allow login)"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !formData.firstName || !formData.lastName || !formData.email}
          >
            {loading ? <CircularProgress size={20} /> : (editingContact ? 'Update Contact' : 'Add Contact')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Are you sure you want to delete this contact? This action cannot be undone and will revoke their portal access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientContacts;

